import type { QuestionSectionProps } from './QuestionSection.types';

import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import * as selectors from 'features/sessions/selectors';
import { useGetQuestionResponseQuery } from 'features/sessions/api';
import { Loading } from 'components';
import { RadioOptions, DragAndDropOptions } from './elements';
import './_question-section.scss';

export const QuestionSection = ({ sessionId, questionId, isSessionCompleted, isHotkeyDisabled, onSubmitResponse }: QuestionSectionProps) => {
  const currentQuestionResponse = useSelector(selectors.selectCurrentQuestionResponse);
  const { isLoading: isLoadingQuestionResponse } = useGetQuestionResponseQuery(
    {
      session_id: sessionId,
      question_id: questionId,
    },
    {
      skip: !questionId,
      refetchOnMountOrArgChange: true,
    },
  );

  const submitResponse = (value: Parameters<typeof onSubmitResponse>[0]) => {
    if (isSessionCompleted) return;
    onSubmitResponse(value);
  };

  if (isLoadingQuestionResponse || !currentQuestionResponse) {
    return <Loading />;
  }

  return (
    <div className="question-section__container">
      <div
        className={classNames('question-section__content', {
          'question-section__content--split':
            currentQuestionResponse.question.situation.split && currentQuestionResponse.question.type !== 'DD',
        })}
      >
        <div className="question-section__situation">
          <div
            className="question-section__situation--text"
            dangerouslySetInnerHTML={{ __html: currentQuestionResponse.question.situation.text ?? '' }}
          />
          {currentQuestionResponse.question.situation.image_url && (
            <img
              className="question-section__situation--img"
              src={currentQuestionResponse.question.situation.image_url}
            />
          )}
        </div>

        <div className="question-section__question">
          <div
            className="question-section__question--text"
            dangerouslySetInnerHTML={{ __html: currentQuestionResponse.question.text ?? '' }}
          />
          {currentQuestionResponse.question.image_url && (
            <img className="question-section__question--img" src={currentQuestionResponse.question.image_url} />
          )}

          {currentQuestionResponse.question.type === 'MC' && (
            <RadioOptions
              question={currentQuestionResponse.question}
              value={currentQuestionResponse.value}
              isSessionCompleted={isSessionCompleted}
              onChange={submitResponse}
              isHotkeyDisabled={isHotkeyDisabled}
            />
          )}

          {currentQuestionResponse.question.type === 'DD' && (
            <DragAndDropOptions
              question={currentQuestionResponse.question}
              value={currentQuestionResponse.value}
              isSessionCompleted={isSessionCompleted}
              onChange={submitResponse}
            />
          )}

          {isSessionCompleted && (
            <div className="question-section__question--explanation">
              <h5>Why?</h5>
              <p dangerouslySetInnerHTML={{ __html: currentQuestionResponse.question.explanation ?? '' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
